'use client'

import { useRef, useEffect, useContext, useState } from 'react'
import { clsx } from 'clsx'
import EmbedRenderer from '@/components/embeds/EmbedDefault/embedRenderer'
import { ConsentContext } from '@/context/ConsentContext'
import { SocialVendor } from '@/types/consent'
import styles from './EmbedDefault.module.scss'
import NoConsent, { socialMap } from './NoConsent'
import { RequestCookie } from 'next/dist/compiled/@edge-runtime/cookies'

export type EmbedData = {
  provider: string
  url: string
  params?: { [key: string]: string }
  cookies: RequestCookie[]
}

const EmbedDefault = ({ provider, url, params, cookies }: EmbedData) => {
  const consentContext = useContext(ConsentContext)
  const consentRequired = !!socialMap[provider as SocialVendor]
  const [showEmbed, setShowEmbed] = useState<boolean>(!consentRequired)
  const [showNoConsent, setShowNoConsent] = useState<boolean>(false)
  const elem = useRef<HTMLDivElement>(null)

  const buildParams = () => {
    let dataParams = ''
    if (params) {
      const keys = Object.keys(params)
      keys.forEach((key, index) => {
        dataParams += key + '=' + params[key]
        if (index < keys.length - 1) dataParams += '&'
      })
    }
    return dataParams
  }

  if (url.startsWith('//')) url = 'https:' + url

  useEffect(() => {
    if (showEmbed === true) {
      EmbedRenderer.configure({
        proxyUrl: 'https://embeds.rtl.de/oembed',
        proxyApiKey: 'uuid',
      })
      if (elem.current) {
        EmbedRenderer.mount(elem.current)
      }
    }
  }, [showEmbed])

  useEffect(() => {
    if (consentRequired && consentContext.consentReady) {
      if (consentContext.userConsents && consentContext.userConsents[provider as SocialVendor]) {
        setShowEmbed(true)
      } else {
        setShowNoConsent(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consentContext.consentReady])

  return (
    <div
      className={clsx(styles[provider])}
      ref={elem}
      data-url={url}
      data-params={buildParams()}
      data-no-consent="true"
      data-test={provider}
    >
      {showNoConsent && (
        <NoConsent
          provider={provider as SocialVendor}
          purMode={consentContext.status === 'purMode'}
          setShowEmbed={setShowEmbed}
          cookies={cookies}
        />
      )}
    </div>
  )
}

export default EmbedDefault
